import React, { useState, useEffect } from 'react';
import { get, post, del } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faTimes, faTrash, faUserPlus } from '@fortawesome/free-solid-svg-icons';

const TrabajadorModal = ({ show, onClose, tiendaId }) => {
  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadorEmail, setTrabajadorEmail] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchTrabajadores(tiendaId);
    }
  }, [show, tiendaId]);

  const fetchTrabajadores = async (tiendaId) => {
    try {
      setLoading(true);
      const response = await get(`tiendas/${tiendaId}/trabajadores`);
      setTrabajadores(response || []);
    } catch (error) {
      console.error('Error fetching trabajadores:', error);
      setTrabajadores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrabajador = async () => {
    if (!trabajadorEmail.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Por favor, ingrese un email válido',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
      return;
    }

    try {
      setLoading(true);
      await post(`trabajadores/add-to-tienda/${tiendaId}`, { email: trabajadorEmail });
      fetchTrabajadores(tiendaId);
      setTrabajadorEmail('');
      Swal.fire({
        title: '¡Éxito!',
        text: 'Trabajador añadido o invitación enviada correctamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
        timer: 2000
      });
    } catch (error) {
      Swal.fire({
        title: 'Error',
        text: error.message || 'No se pudo agregar el trabajador',
        icon: 'error',
        confirmButtonText: 'Aceptar'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTrabajador = async (trabajadorId, trabajadorNombre) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas eliminar a ${trabajadorNombre} de esta tienda?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await del(`trabajadores/${trabajadorId}/tiendas/${tiendaId}`);
        fetchTrabajadores(tiendaId);
        Swal.fire({
          title: 'Eliminado',
          text: 'Trabajador eliminado correctamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          timer: 1500
        });
      } catch (error) {
        Swal.fire({
          title: 'Error',
          text: error.message || 'No se pudo eliminar el trabajador',
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
      } finally {
        setLoading(false);
      }
    }
  };

  if (!show) return null;

  return (
    <div className="fixed z-50 inset-0 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
        {/* Fondo oscuro con blur */}
        <div
          className="fixed inset-0 transition-opacity bg-black bg-opacity-50 backdrop-blur-sm"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Contenido del modal */}
        <div
          className="bg-white rounded-xl shadow-2xl transform transition-all w-full max-w-2xl relative z-10"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header con gradiente azul */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faUser} className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Gestionar Trabajadores</h2>
                  <p className="text-blue-100 text-sm">Añade o elimina trabajadores de esta tienda</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors duration-200 p-2 hover:bg-white hover:bg-opacity-10 rounded-lg"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
          </div>

          {/* Contenido del modal */}
          <div className="px-6 py-6">
            {/* Formulario para añadir trabajador */}
            <div className="bg-blue-50 rounded-lg p-5 mb-6 border border-blue-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faUserPlus} className="text-blue-600 mr-2" />
                Añadir Nuevo Trabajador
              </h3>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddTrabajador();
                }}
                className="space-y-4"
              >
                <div>
                  <label
                    className="block text-sm font-medium text-gray-700 mb-2"
                    htmlFor="trabajadorEmail"
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="text-blue-600 mr-2" />
                    Email del Trabajador
                  </label>
                  <input
                    type="email"
                    name="trabajadorEmail"
                    value={trabajadorEmail}
                    onChange={(e) => setTrabajadorEmail(e.target.value)}
                    required
                    placeholder="trabajador@ejemplo.com"
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={loading}
                    className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <FontAwesomeIcon icon={faUserPlus} />
                    <span>{loading ? 'Añadiendo...' : 'Añadir Trabajador'}</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Lista de trabajadores */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Trabajadores Asignados ({trabajadores.length})
              </h3>

              {loading && trabajadores.length === 0 ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="text-gray-500 mt-2">Cargando trabajadores...</p>
                </div>
              ) : trabajadores.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <FontAwesomeIcon icon={faUser} className="text-4xl text-gray-400 mb-3" />
                  <p className="text-gray-500 font-medium">No hay trabajadores asignados</p>
                  <p className="text-gray-400 text-sm mt-1">Añade trabajadores usando el formulario de arriba</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trabajadores.map((trabajador) => (
                    <div
                      key={trabajador._id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-200 flex items-center justify-between group"
                    >
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <FontAwesomeIcon icon={faUser} className="text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {trabajador.nombre || 'Sin nombre'}
                          </p>
                          <p className="text-sm text-gray-500 truncate flex items-center">
                            <FontAwesomeIcon icon={faEnvelope} className="text-xs mr-1" />
                            {trabajador.email}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteTrabajador(trabajador._id, trabajador.nombre || trabajador.email)}
                        disabled={loading}
                        className="ml-4 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors duration-200 font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        <span className="hidden sm:inline">Eliminar</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrabajadorModal;
